import { ITimeSnapshot } from '../types';

export interface IMemoryConfig {
  maxTotalMemoryMB: number;
  maxSnapshotSizeMB: number;
  compressionEnabled: boolean;
  circularRefHandling: boolean;
  autoCleanupThreshold: number;
  memoryCheckInterval: number;
}

export interface IMemoryUsage {
  totalUsageMB: number;
  snapshotCount: number;
  averageSnapshotSizeMB: number;
  largestSnapshotMB: number;
  compressionRatio: number;
}

export interface IMemoryAlert {
  type: 'warning' | 'critical';
  message: string;
  currentUsageMB: number;
  recommendedAction: string;
  timestamp: number;
}

/**
 * MemoryManager - 스냅샷 메모리 사용량을 관리하고 최적화하는 클래스
 */
export class MemoryManager {
  private _config: IMemoryConfig;
  private _snapshotSizes: Map<string, number> = new Map();
  private _currentMemoryUsage: number = 0;
  private _memoryCheckTimer: number | null = null;
  private _onMemoryAlert?: (alert: IMemoryAlert) => void;

  constructor(config: Partial<IMemoryConfig> = {}) {
    this._config = {
      maxTotalMemoryMB: 100, // 100MB 기본 제한
      maxSnapshotSizeMB: 10,  // 개별 스냅샷 10MB 제한
      compressionEnabled: true,
      circularRefHandling: true,
      autoCleanupThreshold: 0.8, // 80% 도달시 자동 정리
      memoryCheckInterval: 30000, // 30초마다 체크
      ...config,
    };

    this._startMemoryMonitoring();
  }

  /**
   * 스냅샷의 메모리 사용량을 계산합니다
   */
  public calculateSnapshotSize(snapshot: ITimeSnapshot): number {
    try {
      const serialized = this._config.circularRefHandling
        ? this._serializeWithCircularRefHandling(snapshot.state)
        : JSON.stringify(snapshot.state);

      const sizeInBytes = new Blob([serialized]).size;
      const sizeInMB = sizeInBytes / (1024 * 1024);

      this._snapshotSizes.set(snapshot.id, sizeInMB);
      this._updateMemoryUsage();

      return sizeInMB;
    } catch (error) {
      console.warn('Failed to calculate snapshot size:', error);
      return 0;
    }
  }

  /**
   * 스냅샷 압축을 수행합니다
   */
  public compressSnapshot(state: Record<string, unknown>): Record<string, unknown> {
    if (!this._config.compressionEnabled) {
      return state;
    }

    try {
      // 깊은 복사로 원본 보호
      const clonedState = this._deepClone(state);
      
      // 압축 전략들
      return this._applyCompressionStrategies(clonedState);
    } catch (error) {
      console.warn('Compression failed, returning original state:', error);
      return state;
    }
  }

  /**
   * 메모리 정리를 수행합니다
   */
  public cleanup(snapshots: Map<string, ITimeSnapshot>): string[] {
    const removedIds: string[] = [];
    const usage = this.getMemoryUsage();

    // 임계치 초과시 자동 정리
    if (usage.totalUsageMB > this._config.maxTotalMemoryMB * this._config.autoCleanupThreshold) {
      const snapshotsArray = Array.from(snapshots.entries());
      
      // 크기별로 정렬 (큰 것부터)
      snapshotsArray.sort((a, b) => {
        const sizeA = this._snapshotSizes.get(a[0]) || 0;
        const sizeB = this._snapshotSizes.get(b[0]) || 0;
        return sizeB - sizeA;
      });

      // 상위 20% 제거
      const toRemoveCount = Math.ceil(snapshotsArray.length * 0.2);
      
      for (let i = 0; i < toRemoveCount && i < snapshotsArray.length; i++) {
        const [id] = snapshotsArray[i];
        snapshots.delete(id);
        this._snapshotSizes.delete(id);
        removedIds.push(id);
      }

      this._updateMemoryUsage();
      
      this._emitMemoryAlert({
        type: 'warning',
        message: `Memory cleanup performed: ${removedIds.length} snapshots removed`,
        currentUsageMB: this.getMemoryUsage().totalUsageMB,
        recommendedAction: 'Consider reducing snapshot frequency or increasing memory limit',
        timestamp: Date.now(),
      });
    }

    return removedIds;
  }

  /**
   * 스냅샷 제거시 메모리 사용량 업데이트
   */
  public removeSnapshot(snapshotId: string): void {
    this._snapshotSizes.delete(snapshotId);
    this._updateMemoryUsage();
  }

  /**
   * 현재 메모리 사용량 정보를 반환합니다
   */
  public getMemoryUsage(): IMemoryUsage {
    const sizes = Array.from(this._snapshotSizes.values());
    const totalUsageMB = sizes.reduce((sum, size) => sum + size, 0);
    const snapshotCount = sizes.length;

    return {
      totalUsageMB,
      snapshotCount,
      averageSnapshotSizeMB: snapshotCount > 0 ? totalUsageMB / snapshotCount : 0,
      largestSnapshotMB: snapshotCount > 0 ? Math.max(...sizes) : 0,
      compressionRatio: this._calculateCompressionRatio(),
    };
  }

  /**
   * 메모리 알림 콜백을 설정합니다
   */
  public onMemoryAlert(callback: (alert: IMemoryAlert) => void): void {
    this._onMemoryAlert = callback;
  }

  /**
   * 메모리 관리자를 정리합니다
   */
  public destroy(): void {
    if (this._memoryCheckTimer) {
      clearInterval(this._memoryCheckTimer);
      this._memoryCheckTimer = null;
    }
    this._snapshotSizes.clear();
  }

  /**
   * Circular reference를 처리하여 직렬화합니다
   */
  private _serializeWithCircularRefHandling(obj: any): string {
    const seen = new WeakSet();
    
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      
      // React 관련 내부 속성 제거
      if (typeof key === 'string' && key.startsWith('_')) {
        return undefined;
      }
      
      // 함수는 문자열로 변환
      if (typeof value === 'function') {
        return `[Function: ${value.name || 'anonymous'}]`;
      }
      
      // DOM 노드는 요약 정보만
      if (value instanceof Element) {
        return `[Element: ${value.tagName}${value.id ? `#${value.id}` : ''}]`;
      }
      
      return value;
    });
  }

  /**
   * 깊은 복사를 수행합니다
   */
  private _deepClone(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }

    if (obj instanceof Array) {
      return obj.map(item => this._deepClone(item));
    }

    const cloned: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloned[key] = this._deepClone(obj[key]);
      }
    }

    return cloned;
  }

  /**
   * 압축 전략들을 적용합니다
   */
  private _applyCompressionStrategies(state: any): any {
    // 1. 긴 문자열 압축
    state = this._compressLongStrings(state);
    
    // 2. 중복 객체 참조로 변경
    state = this._deduplicateObjects(state);
    
    // 3. 불필요한 속성 제거
    state = this._removeUnnecessaryProps(state);
    
    return state;
  }

  /**
   * 긴 문자열을 압축합니다
   */
  private _compressLongStrings(obj: any): any {
    if (typeof obj === 'string' && obj.length > 1000) {
      // 간단한 압축: 반복 패턴 찾아서 축약
      const compressed = obj.replace(/(.{10,}?)\1{2,}/g, (match, pattern) => {
        const count = Math.floor(match.length / pattern.length);
        return `[REPEAT:${count}x]${pattern}`;
      });
      
      return compressed.length < obj.length ? compressed : obj;
    }
    
    if (typeof obj === 'object' && obj !== null) {
      const result: any = Array.isArray(obj) ? [] : {};
      for (const key in obj) {
        result[key] = this._compressLongStrings(obj[key]);
      }
      return result;
    }
    
    return obj;
  }

  /**
   * 중복 객체를 제거합니다
   */
  private _deduplicateObjects(obj: any): any {
    const objectMap = new Map();
    let refCounter = 0;

    const processObject = (item: any): any => {
      if (typeof item !== 'object' || item === null) {
        return item;
      }

      const serialized = JSON.stringify(item);
      if (objectMap.has(serialized)) {
        return { __ref: objectMap.get(serialized) };
      }

      const refId = `ref_${refCounter++}`;
      objectMap.set(serialized, refId);

      if (Array.isArray(item)) {
        return item.map(processObject);
      }

      const result: any = {};
      for (const key in item) {
        result[key] = processObject(item[key]);
      }

      return result;
    };

    return processObject(obj);
  }

  /**
   * 불필요한 속성들을 제거합니다
   */
  private _removeUnnecessaryProps(obj: any): any {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    const unnecessaryKeys = [
      '__reactInternalInstance',
      '__reactEventHandlers',
      '_owner',
      '_store',
      '$$typeof',
      '__proto__',
    ];

    if (Array.isArray(obj)) {
      return obj.map(item => this._removeUnnecessaryProps(item));
    }

    const result: any = {};
    for (const key in obj) {
      if (!unnecessaryKeys.includes(key) && obj.hasOwnProperty(key)) {
        result[key] = this._removeUnnecessaryProps(obj[key]);
      }
    }

    return result;
  }

  /**
   * 압축률을 계산합니다
   */
  private _calculateCompressionRatio(): number {
    // 실제 구현에서는 압축 전후 크기를 비교
    return this._config.compressionEnabled ? 0.7 : 1.0; // 70% 압축률 추정
  }

  /**
   * 메모리 사용량을 업데이트합니다
   */
  private _updateMemoryUsage(): void {
    this._currentMemoryUsage = Array.from(this._snapshotSizes.values())
      .reduce((sum, size) => sum + size, 0);
  }

  /**
   * 메모리 모니터링을 시작합니다
   */
  private _startMemoryMonitoring(): void {
    this._memoryCheckTimer = window.setInterval(() => {
      const usage = this.getMemoryUsage();
      
      // Critical 알림 (90% 초과)
      if (usage.totalUsageMB > this._config.maxTotalMemoryMB * 0.9) {
        this._emitMemoryAlert({
          type: 'critical',
          message: 'Critical memory usage detected',
          currentUsageMB: usage.totalUsageMB,
          recommendedAction: 'Clear old snapshots immediately',
          timestamp: Date.now(),
        });
      }
      // Warning 알림 (75% 초과)
      else if (usage.totalUsageMB > this._config.maxTotalMemoryMB * 0.75) {
        this._emitMemoryAlert({
          type: 'warning',
          message: 'High memory usage detected',
          currentUsageMB: usage.totalUsageMB,
          recommendedAction: 'Consider clearing old snapshots',
          timestamp: Date.now(),
        });
      }
    }, this._config.memoryCheckInterval);
  }

  /**
   * 메모리 알림을 발생시킵니다
   */
  private _emitMemoryAlert(alert: IMemoryAlert): void {
    if (this._onMemoryAlert) {
      this._onMemoryAlert(alert);
    }
  }
}
